import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { WhatsAppFab } from "../components/WhatsAppFab";
import { applyDetectedLanguage } from "../i18n";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="font-display text-7xl text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="font-display text-3xl text-foreground">Something went wrong</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Please try again or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-full border border-border bg-background px-5 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Private Airport Transfers in Morocco | Bacha Way Travel" },
      { name: "description", content: "Book private airport transfers across Morocco: Casablanca, Marrakech, Fez, Agadir, Tangier & Rabat. Fixed prices, English-speaking drivers, flight tracking, 24/7 WhatsApp support." },
      { name: "author", content: "Bacha Way Travel" },
      { name: "keywords", content: "Morocco airport transfer, Casablanca transfer, Marrakech transfer, Fez transfer, Agadir transfer, private chauffeur Morocco, Moroccan taxi airport, airport pickup Morocco" },
      { property: "og:site_name", content: "Bacha Way Travel" },
      { property: "og:title", content: "Private Airport Transfers in Morocco | Bacha Way Travel" },
      { property: "og:description", content: "Premium private transfers across Morocco — fixed prices, vetted drivers, flight tracking, 24/7 WhatsApp support." },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "en_US" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Private Airport Transfers in Morocco | Bacha Way Travel" },
      { name: "twitter:description", content: "Premium private transfers across Morocco — fixed prices, vetted drivers, flight tracking, 24/7 WhatsApp support." },
      { name: "google-site-verification", content: "aL6AlBF8q4d5yfpj0Q5CqNMOmp6DIOLFt_Yl_kuFXnM" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/jpeg", href: "/__l5e/assets-v1/53ab8690-0b4b-4597-a3a3-5d0fe86b0a32/bacha-logo.jpg" },
      { rel: "apple-touch-icon", href: "/__l5e/assets-v1/53ab8690-0b4b-4597-a3a3-5d0fe86b0a32/bacha-logo.jpg" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "" },
      // Non-render-blocking font load: preload as style, then a tiny inline script (below) swaps media=print -> all when loaded.
      { rel: "preload", as: "style", href: "https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=Fira+Sans:wght@300;400;500;600;700&display=swap" },
      { rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=Fira+Sans:wght@300;400;500;600;700&display=swap", media: "print", "data-font-swap": "1" } as unknown as { rel: string; href: string },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://bachawaytravel.com/#organization",
              name: "Bacha Way Travel",
              url: "https://bachawaytravel.com",
              logo: "https://bachawaytravel.com/__l5e/assets-v1/53ab8690-0b4b-4597-a3a3-5d0fe86b0a32/bacha-logo.jpg",
              sameAs: ["https://wa.me/212600876933"],
              contactPoint: [{
                "@type": "ContactPoint",
                telephone: "+212-600-876-933",
                contactType: "customer service",
                areaServed: "MA",
                availableLanguage: ["English", "French", "Spanish", "Arabic"],
              }],
              areaServed: { "@type": "Country", name: "Morocco" },
            },
            {
              "@type": "WebSite",
              "@id": "https://bachawaytravel.com/#website",
              url: "https://bachawaytravel.com",
              name: "Bacha Way Travel",
              publisher: { "@id": "https://bachawaytravel.com/#organization" },
              inLanguage: "en",
            },
            {
              "@type": "LocalBusiness",
              "@id": "https://bachawaytravel.com/#localbusiness",
              name: "Bacha Way Travel",
              image: "https://bachawaytravel.com/__l5e/assets-v1/53ab8690-0b4b-4597-a3a3-5d0fe86b0a32/bacha-logo.jpg",
              telephone: "+212-600-876-933",
              priceRange: "$$",
              address: { "@type": "PostalAddress", addressCountry: "MA" },
              areaServed: ["Casablanca", "Marrakech", "Fez", "Agadir", "Tangier", "Rabat", "Morocco"],
            },
          ],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
        <script
          dangerouslySetInnerHTML={{
            __html: `(function(){if(window.location.hostname.endsWith('.lovable.app')){window.location.replace('https://www.bachawaytravel.com'+window.location.pathname+window.location.search+window.location.hash);}})();`,
          }}
        />
        {/* Swap deferred font stylesheets from media=print -> all once loaded (non-render-blocking). */}
        <script
          dangerouslySetInnerHTML={{
            __html: `(function(){function s(){document.querySelectorAll('link[rel=stylesheet][data-font-swap]').forEach(function(l){if(l.media!=='all'){l.media='all';}});}if(document.readyState!=='loading'){s();}else{document.addEventListener('DOMContentLoaded',s);}window.addEventListener('load',s);})();`,
          }}
        />
        {/* ===== Google Analytics 4 ===== */}
        <script async src="https://www.googletagmanager.com/gtag/js?id=G-15JZ4WS1F2" />
        <script dangerouslySetInnerHTML={{ __html: `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','G-15JZ4WS1F2');` }} />
        {/* ===== Meta / Facebook Pixel — placeholder. Replace PIXEL_ID with your Pixel ID =====
        <script dangerouslySetInnerHTML={{ __html: `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','PIXEL_ID');fbq('track','PageView');` }} />
        */}
      </head>
      <body suppressHydrationWarning>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isAdmin = pathname.startsWith("/admin");

  useEffect(() => { applyDetectedLanguage(); }, []);


  if (isAdmin) {
    return (
      <QueryClientProvider client={queryClient}>
        <Outlet />
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <div className="flex min-h-screen flex-col">
        <Header />
        <main className="flex-1">
          <Outlet />
        </main>
        <Footer />
        <WhatsAppFab />
      </div>
    </QueryClientProvider>
  );
}
